import React, {useEffect, useState} from 'react';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import {Box, Button, Typography} from '@material-ui/core';
import {
  ISupplierCompanyProfile,
  IDepot,
  ISupplierCompanyPermissionsInput,
  ISupplierUserInput,
  ISupplierUserProfile,
  IGenericType,
} from 'constants/interfaces';
import {CompanyForm} from 'components/CompanyForm/CompanyForm';
import {DepotForm} from 'components/DepotForm/DepotForm';
import {UserForm} from 'components/UserForm/UserForm';
import CheckIcon from 'components/CheckIcon/CheckIcon';
import {CompanyPermissionsForm} from 'components/CompanyForm/CompanyPermissionsForm';
import {apiGetBankAccountTypes, apiGetBankTypes} from 'constants/Api';

const steps = [
  'Create Company',
  'Company Permissions',
  'Create Depot',
  'Add Admin User',
];

export type InviteSupplierFormSubmission = {
  company: ISupplierCompanyProfile;
  companyPermissions: ISupplierCompanyPermissionsInput;
  defaultDepot: IDepot;
  adminUser: ISupplierUserInput;
  /** Whether this company is a Commodity Group or a standard Supplier */
  isCommodityGroup: boolean;
};

export function InviteSupplierForm(props: {
  user: ISupplierUserProfile;
  isLoading: boolean;
  onSubmit: (submission: InviteSupplierFormSubmission) => any;
  isSuccess: boolean;
  onReset: () => any;
}) {
  const [activeStep, setActiveStep] = React.useState(0);
  const [company, setCompany] = React.useState<ISupplierCompanyProfile>();
  const [companyPermissions, setCompanyPermissions] =
    React.useState<ISupplierCompanyPermissionsInput>();
  const [defaultDepot, setDefaultDepot] = React.useState<IDepot>();
  const [adminUser, setAdminUser] = React.useState<ISupplierUserInput>();

  // Commodity Group vs Supplier toggle — defaults to standard Supplier
  const [isCommodityGroup, setIsCommodityGroup] = useState<boolean>(false);

  const [isLoadingBankInfo, setIsLoadingBankInfo] = useState(true);
  const [bankTypes, setBankTypes] = useState<IGenericType[]>([]);
  const [bankAccountTypes, setBankAccountTypes] = useState<IGenericType[]>([]);

  useEffect(() => {
    setIsLoadingBankInfo(true);
    apiGetBankTypes()
      .then(setBankTypes)
      .then(apiGetBankAccountTypes)
      .then(setBankAccountTypes)
      .catch((error) => {
        alert('Failed to load bank information. Please try again later');
        console.error(error);
      })
      .finally(() => setIsLoadingBankInfo(false));
  }, [props.user]);

  useEffect(() => {
    if (props.isSuccess) setActiveStep(-1);
  }, [props.isSuccess]);

  const handleReset = () => {
    setActiveStep(0);
    setCompany(undefined);
    setCompanyPermissions(undefined);
    setDefaultDepot(undefined);
    setAdminUser(undefined);
    setIsCommodityGroup(false);
    props.onReset();
  };

  const handleCompanySubmit = (company: ISupplierCompanyProfile) => {
    setCompany(company);
    handleNext();
  };

  const handleCompanyPermissionsSubmit = (
    companyPermissions: ISupplierCompanyPermissionsInput,
  ) => {
    setCompanyPermissions(companyPermissions);
    handleNext();
  };

  const handleDepotSubmit = (depot: IDepot) => {
    setDefaultDepot(depot);
    handleNext();
  };

  const handleAdminUserSubmit = (user: ISupplierUserInput) => {
    user.address = [
      defaultDepot?.address1,
      defaultDepot?.address2,
      defaultDepot?.address3,
      defaultDepot?.address4,
      defaultDepot?.address5,
    ]
      .filter((i) => i)
      .join(',');
    user.latlong = defaultDepot?.latitude + ',' + defaultDepot?.longitude;
    setAdminUser(user);
    const submission: InviteSupplierFormSubmission = {
      company: company!,
      companyPermissions: companyPermissions!,
      defaultDepot: defaultDepot!,
      adminUser: user,
      isCommodityGroup,
    };
    handleFinalSubmit(submission);
  };

  const handleFinalSubmit = (submission: InviteSupplierFormSubmission) => {
    props.onSubmit(submission);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getStepContent = () => {
    switch (activeStep) {
      case -1:
        return (
          <SuccessForm isLoading={props.isLoading} onReset={handleReset} />
        );
      case 0:
        return (
          <CompanyForm
            user={props.user}
            company={company}
            isLoading={props.isLoading}
            submitButtonTitle={'Next'}
            onSubmit={handleCompanySubmit}
            cancelButtonTitle={'Reset'}
            onCancel={handleReset}
            editMargin
            isLoadingBankInfo={isLoadingBankInfo}
            bankTypes={bankTypes}
            bankAccountTypes={bankAccountTypes}
            isCommodityGroup={isCommodityGroup}
            onCompanyTypeChange={setIsCommodityGroup}
          />
        );
      case 1:
        return (
          <Box mt={2}>
            <CompanyPermissionsForm
              companyPermissions={companyPermissions}
              isLoading={props.isLoading}
              submitButtonTitle={'Next'}
              onSubmit={handleCompanyPermissionsSubmit}
              cancelButtonTitle={'Back'}
              onCancel={handleBack}
            />
          </Box>
        );
      case 2:
        return (
          <Box mt={2}>
            <DepotForm
              depot={defaultDepot}
              isLoading={props.isLoading}
              submitButtonTitle={'Next'}
              onSubmit={handleDepotSubmit}
              cancelButtonTitle={'Back'}
              onCancel={handleBack}
            />
          </Box>
        );
      case 3:
        return (
          <UserForm
            loggedInUser={props.user}
            depots={[]}
            user={adminUser}
            isLoading={props.isLoading}
            submitButtonTitle={'Finish'}
            onSubmit={handleAdminUserSubmit}
            cancelButtonTitle={'Back'}
            onCancel={handleBack}
            shouldCapturePassword
            hideDepotFields
          />
        );
      default:
        throw Error('Unhandled form step: ' + activeStep);
    }
  };

  return (
    <Box className="invite-supplier-form-container">
      <Stepper
        className="stepper-container"
        activeStep={activeStep}
        variant="outlined"
      >
        {steps.map((label) => {
          return (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Box className="step-content-container">
        <Box
          className="step-content"
          style={activeStep === 1 ? {width: '100%'} : {}}
        >
          {getStepContent()}
        </Box>
      </Box>
    </Box>
  );
}

function SuccessForm(props: {isLoading: boolean; onReset: () => any}) {
  return (
    <Box display="flex" flexDirection="column" alignItems="center" p={3}>
      <CheckIcon />
      <Box mt={2} mb={4}>
        <Typography variant="subtitle1">
          Successfully onboarded supplier
        </Typography>
      </Box>
      <Button
        className={'primary-btn'}
        variant="outlined"
        color="primary"
        disabled={props.isLoading}
        onClick={props.onReset}
      >
        Onboard Another Supplier
      </Button>
    </Box>
  );
}
